import {
  ChangeLog,
  ChangeLogMetadata,
  ChangeReviewStatus,
  ChangeSource,
  GymImagesMetadata,
} from '../model/changelog';
import moment from 'moment';
import { GymImage } from '../model/gymImage';
import { NetworkType } from '../model/gym';

export class ChangelogUtil {
  public static formatLogsToDisplay(changeLogs: ChangeLog[]): ChangeLog[] {
    return changeLogs.map(log => {
      return {
        ...log,
        formattedFields: this.getFields(log.metadata),
        formattedCreatedAt: this.formatDateTime(log.createdAt),
        formattedSource: this.getDisplaySource(log.source),
        formattedReviewStatus: this.getReviewStatus(
          log.source,
          log.reviewStatus
        ),
        formattedNetwork: this.getDisplayNetwork(log.network),
      } as ChangeLog;
    });
  }

  public static getDisplayNetwork(network?: NetworkType): string {
    switch (network) {
      case NetworkType.DACH:
        return 'DACH';
      case NetworkType.FR_BE:
        return 'FR&BE';
      case NetworkType.US:
        return 'US';
      default:
        return '-';
    }
  }

  private static getFields(metadataList: ChangeLogMetadata[]) {
    return metadataList
      .map(metadata => {
        metadata.formattedField = this.formatField(metadata.field);
        return metadata.formattedField;
      })
      .join(', ');
  }

  private static formatField(field: string) {
    const words = field.split(/(?=[A-Z])/);
    return words.reduce((accumulator, current) => {
      if (accumulator === '') {
        return current.charAt(0).toUpperCase() + current.slice(1);
      }
      return accumulator.concat(` ${current.toLowerCase()}`);
    }, '');
  }

  private static formatDateTime(time?: number) {
    return time ? moment(new Date(time)).format('HH:mm - DD.MM.YYYY') : '';
  }

  private static getDisplaySource(source: ChangeSource) {
    return source === ChangeSource.INTERNAL_ADMIN
      ? 'Internal Admin'
      : 'Business Suite';
  }

  private static getReviewStatus(
    source: ChangeSource,
    reviewStatus: ChangeReviewStatus
  ) {
    if (source === ChangeSource.INTERNAL_ADMIN) {
      return 'No review needed';
    }
    return reviewStatus === ChangeReviewStatus.VIEWED ? 'Viewed' : 'Not Viewed';
  }

  private static formatObjectValue(value: any): string | null {
    if (value === undefined) {
      return null;
    }
    if (Array.isArray(value)) {
      return `[${value.map(v => this.formatObjectValue(v)).join(', ')}]`;
    }
    if (typeof value === 'object') {
      return `{ ${Object.entries(value)
        .map(([k, v]) => `${k}: ${this.formatObjectValue(v)}`)
        .join(', ')} }`;
    }
    return String(value);
  }

  public static formatChangeLogValues(
    metadata: ChangeLogMetadata
  ): ChangeLogMetadata {
    if (metadata.field === 'gymImages') {
      return {
        ...metadata,
        oldValue: this.separateDeletedImages(metadata),
        newValue: {
          images: [...metadata.newValue],
        } as GymImagesMetadata,
      };
    }
    if (metadata.field === 'creditsConfig') {
      return {
        ...metadata,
        oldValue: this.formatObjectValue(metadata.oldValue),
        newValue: this.formatObjectValue(metadata.newValue),
      };
    }
    return metadata;
  }

  private static separateDeletedImages(
    metadata: ChangeLogMetadata
  ): GymImagesMetadata {
    return (
      metadata.oldValue?.reduce(
        (diff: GymImagesMetadata, oldImage: GymImage) => {
          if (
            metadata.newValue.some(
              (i: GymImage) => i.imageId === oldImage.imageId
            )
          ) {
            diff.images.push(oldImage);
          } else {
            diff.deletedImages.push(oldImage);
          }
          return diff;
        },
        { images: [], deletedImages: [] }
      ) ?? {}
    );
  }

  public static isGymImagesValueEmpty(value: GymImagesMetadata) {
    return (
      !value ||
      (value.images?.length === 0 && value.deletedImages?.length === 0)
    );
  }
}
